import React, { useEffect, useState } from 'react';



import { makeStyles } from 'tss-react/mui';
import { TFunction, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  Box,
  ContentTemplate,
  Divider,
  Grid,
  HeaderTemplate,
  KeyValue,
  MUIDataTable,
  MUIDataTableColumnDef,
  Progress,
  SectionHeader,
  SingleSelectionRadio,
  SingleSelectionRadioProps,
  Stepper,
  TitleHeader,
  Typography,
} from 'web-library';
import { PaymentAccessResponse } from 'web-library/models/Payment';
import { AppealDraftReviewResponse } from '../../../api/candidate/appeal/model';

import ExamLevelDetails from '../../../modules/appeal/components/@candidate/ExamLevelDetails';
import PersonalDetails from '../../../modules/appeal/components/@candidate/PersonalDetails';
import AppealFees from '../../../modules/appeal/components/@candidate/AppealFees';
import FormHeader from '../../../components/form-items/FormHeader';
import Declaration from '../../../components/Declaration';
import {
  ExamPaymentMode,
  ExamPaymentModeKey,
} from '../../../api-refactored/group-exam-registration/usePostReviewGroupExamRegistration';
import {
  CoordinatorAppealReviewResponse,
  useGetCoordinatorAppealReview,
} from '../../../api-refactored/appeal/useGetCoordinatorAppealReview';
import CoordinatorReviewAppealButtons from './CoordinatorReviewAppealButtons';
import { useGetPaymentAccess } from '../../../api-refactored/financial/useGetPaymentAccess';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    left: '-9.5%',
    [theme.breakpoints.between('sm', 'lg')]: {
      left: '-10%',
    },
    [theme.breakpoints.between('xs', 'md')]: {
      left: '-5%',
    },
  },
  container: {
    alignItems: 'center',
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  dialogBold: {
    fontWeight: 'bold',
  },
  paymentModeContainer: {
    marginBottom: theme.spacing(6),
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  feeColumnHeader: {
    textAlign: 'right',
  },
  buttonContainer: {
    marginBottom: theme.spacing(7),
  },
  divider: {
    marginTop: theme.spacing(8),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(5),
    },
  },
  pleaseLogoutText: {
    marginBottom: theme.spacing(6),
  },
}));

const checkAvailablePaymentMode = (paymentDataOption: ExamPaymentMode): [] | ExamPaymentModeKey[] =>
  (Object.keys(paymentDataOption) as ExamPaymentModeKey[]).filter((key) => paymentDataOption[key]);

const transformPayOption = (
  paymentDataOption: ExamPaymentMode,
  t: TFunction<'translation'>
): SingleSelectionRadioProps['data'] =>
  checkAvailablePaymentMode(paymentDataOption).map((key: ExamPaymentModeKey) => {
    if (key === ExamPaymentModeKey.bankTransferOption) {
      return {
        label: t('appeal->>AppealCoordinatorReviewApplication.BankTransfer'),
        value: key,
      };
    }
    if (key === ExamPaymentModeKey.coordinatorPayOption) {
      return {
        label: t('appeal->>AppealCoordinatorReviewApplication.PayForCandidates'),
        value: key,
      };
    }
    if (key === ExamPaymentModeKey.candidatePayOption) {
      return {
        label: t('appeal->>AppealCoordinatorReviewApplication.CandidatesMakePayment'),
        additionalLabel: t(
          'appeal->>AppealCoordinatorReviewApplication.CandidatesMakePaymentDescription'
        ),
        value: key,
      };
    }
    return {
      label: '',
      additionalLabel: '',
      value: ExamPaymentModeKey.noPaymentModeSelected,
    };
  });

const paymentDataOptions = (
  reviewDraftData: CoordinatorAppealReviewResponse,
  paymentAccessDetails: PaymentAccessResponse
) =>
({
  /** Order is important here to reflect in the UI, DO NOT change the order * */
  bankTransferOption: reviewDraftData.bankTransferOption,
  coordinatorPayOption:
    paymentAccessDetails?.creditCardMethod || paymentAccessDetails?.payNowMethod,
  candidatePayOption: paymentAccessDetails?.candidatePayMethod,
} as ExamPaymentMode);

export const generateColumns = (
  reviewDraftData: AppealDraftReviewResponse,
  classes: Record<string,string>,
  t: any
): MUIDataTableColumnDef[] => [
    {
      label: t('appeal->>AppealReview.Subject Name'),
      name: 'nameDescription',
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          const { nameDescription, nameCode } = reviewDraftData.selectedSubjects.subjectInfo[
            dataIndex
          ];
          return (
            <>
              <>{nameDescription} </>
              <>{`(${nameCode})`}</>
            </>
          );
        },
      },
    },
    {
      label: t('appeal->>AppealReview.Grade Alphabetical'),
      name: 'gradeAlphabetical',
    },
    {
      label: t('appeal->>AppealReview.Grade Numerical'),
      name: 'gradeNumerical',
    },
    {
      label: t('appeal->>AppealReview.Language Medium'),
      name: 'languageMedium',
    },
    {
      name: 'appealFee',
      options: {
        customHeadLabelRender: () => (
          <>
            <Grid container justify="flex-end" className={classes.feeColumnHeader}>
              {t('appeal->>AppealReview.Appeal Fees Payable')}
              <br />
              {t('appeal->>AppealReview.(incl GST) (SGD)')}
            </Grid>
          </>
        ),
        customBodyRender: (value: string) => (
          <Grid container justify="flex-end">
            {`S$${value}`}
          </Grid>
        ),
      },
    },
  ];

const handleAgreement = (
  setErrMsg: React.Dispatch<React.SetStateAction<boolean>>,
  setIsDeclared: React.Dispatch<React.SetStateAction<boolean>>,
  isDeclared: boolean
) => {
  setErrMsg(false);
  setIsDeclared(!isDeclared);
};

const displayErrorMessage = (
  paymentModeNotSelectedErrMsg: boolean,
  errMsg: boolean,
  classes: Record<string,string>,
  t: TFunction<'translation'>
) => {
  if (paymentModeNotSelectedErrMsg && errMsg) {
    return (
      <>
        <Grid xs={12} md={8}>
          <Typography className={classes.errorText} display="inline" variant="body2">
            {t('appeal->>AppealCoordinatorReviewApplication.PaymentModeNotSelected')}
          </Typography>
        </Grid>
        <Grid xs={12} md={8}>
          <Typography className={classes.errorText} display="inline" variant="body2">
            {t('components->>Declaration.Please confirm declaration before you proceed.')}
          </Typography>
        </Grid>
      </>
    );
  }
  if (paymentModeNotSelectedErrMsg) {
    return (
      <Grid xs={12} md={8}>
        <Typography className={classes.errorText} display="inline" variant="body2">
          {t('appeal->>AppealCoordinatorReviewApplication.PaymentModeNotSelected')}
        </Typography>
      </Grid>
    );
  }
  if (errMsg) {
    return (
      <Grid xs={12} md={8}>
        <Typography className={classes.errorText} display="inline" variant="body2">
          {t('components->>Declaration.Please confirm declaration before you proceed.')}
        </Typography>
      </Grid>
    );
  }
  return null;
};

interface CoordinatorReviewAppealProps {
  title: string;
  routeSteps: { label: string }[];
}

const CoordinatorReviewAppeal = ({ title, routeSteps }: CoordinatorReviewAppealProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();

  const [errMsg, setErrMsg] = useState<boolean>(false);
  const [paymentModeNotSelectedErrMsg, setPaymentModeNotSelectedErrMsg] = useState<boolean>(false);
  const [isDeclared, setIsDeclared] = useState<boolean>(false);
  const [isPaymentDisabled, setIsPaymentDisabled] = useState<boolean>(false);

  const [selectedPaymentMode, setSelectedPaymentMode] = useState<ExamPaymentModeKey>(
    ExamPaymentModeKey.noPaymentModeSelected
  );

  const { data: paymentAccessDetails } = useGetPaymentAccess();

  const {
    data: reviewDraftData,
    isLoading: isLoadingReviewDraftData,
  } = useGetCoordinatorAppealReview({ uuid });


  useEffect(() => {
    if (
      selectedPaymentMode === ExamPaymentModeKey.coordinatorPayOption ||
      ExamPaymentModeKey.bankTransferOption ||
      ExamPaymentModeKey.candidatePayOption
    ) {
      setPaymentModeNotSelectedErrMsg(false);
    }
  }, [selectedPaymentMode]);

  useEffect(() => {
    if (
      reviewDraftData &&
      !reviewDraftData.bankTransferOption &&
      !reviewDraftData.coordinatorPayOption &&
      !reviewDraftData.candidatePayOption
    ) {
      setIsPaymentDisabled(true);
    } else {
      setIsPaymentDisabled(false);
    }
  }, [reviewDraftData, selectedPaymentMode]);

  return (
    <>
      <TitleHeader title={title} />
      <HeaderTemplate>
        {reviewDraftData && (
          <KeyValue
            keyText={t('appeal->>AppealCoordinatorSelectSubject.ExamLevel')}
            valueText={reviewDraftData.selectedSubjects.examLevel}
          />
        )}
        <Box className={classes.stepper}>
          <Stepper activeStep={2} steps={routeSteps} />
        </Box>
      </HeaderTemplate>
      {isLoadingReviewDraftData ? (
        <ContentTemplate>
          <Progress color="secondary" />
        </ContentTemplate>
      ) : (
        <Box>
          {reviewDraftData && (
            <>
              <ContentTemplate isContinuable type="full">
                <SectionHeader title={t('appeal->>ReviewAppeal.Review Application')} />
                <PersonalDetails {...reviewDraftData.candidateDetails} />
                <Divider />
                <ExamLevelDetails
                  formHeader={t('appeal->>ReviewAppeal.Selected Subject(s)')}
                  {...reviewDraftData.selectedSubjects}
                />
                <Grid sm={12}>
                  <MUIDataTable
                    columns={generateColumns(reviewDraftData, classes, t)}
                    data={reviewDraftData.selectedSubjects.subjectInfo}
                    options={[]}
                    components={[]}
                  />
                  <Divider className={classes.divider} />
                </Grid>
                <AppealFees {...reviewDraftData.appealFees} />
                <Box id="payment-mode" className={classes.paymentModeContainer}>
                  <Divider />
                  <Typography variant="h3">{t('exam->>ReviewExam.Payment Mode')}</Typography>
                  {paymentAccessDetails && (
                    <SingleSelectionRadio
                      data={transformPayOption(
                        paymentDataOptions(reviewDraftData, paymentAccessDetails),
                        t
                      )}
                      onChange={(event) =>
                        setSelectedPaymentMode(event.target.value as ExamPaymentModeKey)
                      }
                      value={selectedPaymentMode}
                    />
                  )}
                </Box>
                <Divider />
                <FormHeader title={t('appeal:ReviewAppeal.Declaration')} />
                <Declaration
                  noTitle
                  isAgreed={isDeclared}
                  handleAgreement={() => handleAgreement(setErrMsg, setIsDeclared, isDeclared)}
                  customContent={t(
                    'appeal->>AppealCoordinatorReviewApplication.AppealDeclarationStatement'
                  )}
                />
                {displayErrorMessage(paymentModeNotSelectedErrMsg, errMsg, classes, t)}
              </ContentTemplate>
              <CoordinatorReviewAppealButtons
                classes={classes}
                uuid={uuid}
                isPaymentDisabled={isPaymentDisabled}
                setIsPaymentDisabled={setIsPaymentDisabled}
                selectedPaymentMode={selectedPaymentMode}
                isDeclared={isDeclared}
                setErrMsg={setErrMsg}
                setPaymentModeNotSelectedErrMsg={setPaymentModeNotSelectedErrMsg}
              />
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default CoordinatorReviewAppeal;
